<template>
  <div>
    <h3 class="text-xl font-bold text-neutral-900">
      {{ $t('Widget') }} <sup class="beta">{{ $t('Beta') }}</sup>
    </h3>
    <p class="text-neutral-600 mt-1">
      {{ $t("FeedBear Widget lets you embed the idea form anywhere on your site. This allows your users to submit their ideas without leaving what they're doing. The setup and configuration of the widget require some changes to your code.") }}
    </p>

    <PlanAlert v-if="!widgetAllowed" :title="$t('Available on paid plans')" :description="$t('The widget is available on paid plans. Pick a plan to activate it on your site.')" :link="'/edit?view=billing'" :click="$t('View plans')" />

    <div class="mb-5 bg-yellow-100 border border-yellow-400 p-4 rounded-xl mt-5 text-yellow-900 text-sm" v-if="!this.projectJwt">
      <p>
        <strong>{{ $t('Using a new key instead of the SSO key') }}</strong><br/>
        {{ $t("If you don't have a new key, we'll keep using the SSO key.") }}<br/>{{ $t("For new projects, a new private key is generated. When a new key is generated, the SSO key won't be used for JWT in the Widget.") }}
      </p>
    </div>

    <h4 class="mt-8 font-semibold text-neutral-900">
      {{ $t('1. Configure the widget') }}
    </h4>
    <p class="text-neutral-600 mt-1">
      {{ $t('Pick a board you want to use and add the following code right before the closing') }}
      <code>&#x3C;body&#x3E;</code> {{ $t('tag:') }}
    </p>

     <div class="mt-2">
      <label for="board-select" class="label">{{ $t('Fill board id for:') }}</label>
      <div class="relative max-w-md">
        <select
          name="board"
          id="board-select"
          class="select"
          v-model="selectedBoard"
          ref="boardSelect"
        >
          <option
            :value="board.slug"
            v-for="board in this.project.boards"
            :key="board.id"
          >
            {{ board.name }}
          </option>
          <option
            value="All boards"
            key="All boards"
          >
            {{ $t('All boards') }}
          </option>
        </select>
      </div>
    </div>

    <div class="flex flex-wrap">
      <div class="w-full">
        <div class="">
          <div class="pb-5 pt-2 flex-auto">
            <div class=" sso-webhook-style">
              <div>
                <prism-editor class="prism-code-editor rounded" v-model="scriptCode" :line-numbers="lineNumbers" :highlight="highlighter" readonly></prism-editor>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <h4 class="mt-8 font-semibold text-neutral-900">
      {{ $t('2. Hook FeedBear to an element') }}
    </h4>
    <p class="text-neutral-600 mt-1">
      {{ $t('Pick an element (usually a button or a link) that will trigger FeedBear widget popup on click. When using a default configuration, simply add') }}
      <code>data-feedbear-button</code> {{ $t('to that element. For example:') }}
    </p>
    
    <div class="flex flex-wrap">
      <div class="w-full">
        <div class="">
          <div class="pb-5 pt-2 flex-auto">
            <div class=" sso-webhook-style">
              <div>
                <prism-editor class="prism-code-editor rounded" v-model="scriptCode1" :line-numbers="lineNumbers" :highlight="highlighter" readonly></prism-editor>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <h4 class="mt-8 font-semibold text-neutral-900">
      {{ $t('3. Configure the JWT (optional)') }}
    </h4>
    <p class="text-neutral-600 mt-1">
      {{ $t('JWT allows your users to post feedback using their existing accounts without leaving your site.') }}
    </p>
    <p class="text-neutral-600 mt-2">
      {{ $t("Here's your") }} <strong class="text-neutral-800">{{ $t('private signature key') }}</strong>{{ $t(':') }}
    </p>
    <div class="flex mt-2 inp-width ">
      <div class="relative flex justify-between items-center input ">
        <div class="parentInput">
          <input
            v-model="projectToken"
            class="childInput"
            :readonly="true"
          />
        </div>
        <tippy
          interactive
          :animate-fill="false"
          theme="translucent"
          animation="fade"
          trigger="click"
          arrow="roundArrow"
        >
          <template v-slot:trigger>
            <div @click="clipCopy" class="cursor-pointer">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
            </div>
          </template>
          <span>
            <p class="font-bold">{{ $t('COPIED!') }}</p>
          </span>
        </tippy>
      </div>
      <button class="btn-danger-outline whitespace-nowrap ml-2" @click.prevent="generateNewKeyPopup = true">{{ $t('Generate new key') }}</button>
    </div>
    <!-- <p class="mt-2 text-neutral-800">
      Follow our guide,
      <a
        href="https://help.feedbear.com/article/25-sso"
        data-beacon-article-sidebar="5f32bfeb042863444aa02a10"
        target="_blank"
        >How to set up Single Sign-On</a
      >
      to generate SSO token on your server.
    </p> -->

    <h4 class="mt-8 font-semibold text-neutral-900">{{ $t('Questions?') }}</h4>
    <p class="text-neutral-600 mt-1">
      {{ $t('If you have any questions or issues,') }}
      <a href="#" @click.prevent="openBeacon()" class="text-project-500 hover:underline">{{ $t('contact us') }}</a> {{ $t('using the button in the bottom right or send an email to') }}
      <a href="mailto:support@feedbear.com" target="_blank" class="text-project-500 hover:underline">support@feedbear.com</a>{{ $t('.') }}
    </p>
    <Dialog v-model="generateNewKeyPopup" :title="$t('Generate new key')" custom-width-class="w-3/12">
      <div class="p-4 pt-2 sm:px-6">
        <div class="p-2 pl-0">
          {{ $t('Are you sure you want to generate a new key?') }}<br> {{ $t('Once a new key is generated your old key will be deleted, this is not reversible.') }}
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <a
            href="#"
            class="btn-secondary whitespace-nowrap"
            @click.prevent="generateNewKeyPopup = false"
          >
            {{ $t('Cancel') }}</a
          >
          <a
            href="#"
            class="btn-danger whitespace-nowrap"
            @click.prevent="regenerateFeedbearToken"
          >
            {{ $t('Generate new key') }}</a
          >
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import 'highlight.js/styles/github.css';
import 'vue-prism-editor/dist/prismeditor.min.css';
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism-tomorrow.css";
import Dialog from "../../components/dialog";
import PlanAlert from "./plan_alert";
import {mapGetters} from "vuex";

export default {
  props: ["project","projectToken", "projectUpdateUrl", "projectJwt"],
  data: function () {
    return {
      selectedBoard: this.project.boards[0].slug,
      //selectedBoard: 'All boards',
      codes: '',
      lineNumbers: true,
      generateNewKeyPopup: false,
      boards_text: '',
    };
  },

  mounted() {
    this.$refs.boardSelect.selectedIndex = 0;
    Beacon("suggest", ["5f328f5d042863444aa0250e", "5f32bfeb042863444aa02a10"]);
  },
  computed: {
    ...mapGetters(['getCurrentProjectPlan']),
    // Same rule as the server's subscription_valid_strict?: a trial is not paid.
    widgetAllowed() {
      const plan = this.getCurrentProjectPlan;
      return !!(plan && plan.name !== "trial" && plan.active_status);
    },
    scriptCode1() {
      return this.codes = `<button data-feedbear-button>Feedback?</button>`;
    },
    scriptCode() {
      this.boards_text = this.project.boards.map(function(board) {
        return board['slug'];
      });
      if (this.projectJwt && this.selectedBoard == 'All boards') {
        return this.codes = `<script>
  (function (w, d, s, o, f, js, fjs) { w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments) }; js = d.createElement(s), fjs = d.getElementsByTagName(s)[0]; js.id = o; js.src = f; js.async = 1; fjs.parentNode.insertBefore(js, fjs); }(window, document, 'script', 'FeedBear', 'https://sdk.feedbear.com/widget.js'));
  FeedBear("button", {
  element: document.querySelector("[data-feedbear-button]"),
  project: "`+ this.project.subdomain +`",
  boards: "`+ this.boards_text +`",
  jwt: null // see step 3,
  });
 <\/script>`;
      } else if (this.projectJwt && this.selectedBoard != 'All boards') {
        return this.codes = `<script>
  (function (w, d, s, o, f, js, fjs) { w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments) }; js = d.createElement(s), fjs = d.getElementsByTagName(s)[0]; js.id = o; js.src = f; js.async = 1; fjs.parentNode.insertBefore(js, fjs); }(window, document, 'script', 'FeedBear', 'https://sdk.feedbear.com/widget.js'));
  FeedBear("button", {
  element: document.querySelector("[data-feedbear-button]"),
  project: "` + this.project.subdomain + `",
  board: "` + this.selectedBoard + `",
  jwt: null // see step 3,
  });
<\/script>`;
      } else if (!this.projectJwt && this.selectedBoard != 'All boards'){
          return this.codes = `<script>
  (function (w, d, s, o, f, js, fjs) { w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments) }; js = d.createElement(s), fjs = d.getElementsByTagName(s)[0]; js.id = o; js.src = f; js.async = 1; fjs.parentNode.insertBefore(js, fjs); }(window, document, 'script', 'FeedBear', 'https://sdk.feedbear.com/widget.js'));
  FeedBear("button", {
  element: document.querySelector("[data-feedbear-button]"),
  project: "`+ this.project.subdomain +`",
  board: "`+ this.selectedBoard +`",
  ssoToken: null // see step 3,
  });
<\/script>`;
      } else if (!this.projectJwt && this.selectedBoard == 'All boards') {
        return this.codes = `<script>
  (function (w, d, s, o, f, js, fjs) { w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments) }; js = d.createElement(s), fjs = d.getElementsByTagName(s)[0]; js.id = o; js.src = f; js.async = 1; fjs.parentNode.insertBefore(js, fjs); }(window, document, 'script', 'FeedBear', 'https://sdk.feedbear.com/widget.js'));
  FeedBear("button", {
  element: document.querySelector("[data-feedbear-button]"),
  project: "`+ this.project.subdomain +`",
  boards: "`+ this.boards_text +`",
  ssoToken: null // see step 3,
  });
 <\/script>`;
      }
    }
  },

  components: {
    PlanAlert,
    Dialog,
  },

  methods: {
    openBeacon() {
      Beacon("open");
    },
    highlighter(code) {
      return highlight(code, languages.js);
    },
    clipCopy () {
      navigator.clipboard.writeText(this.projectJwt ? this.projectJwt : this.projectToken);
    },
    regenerateFeedbearToken(){
      this.generateNewKeyPopup = false;
      var data = new FormData();
      data.append("project[token_type]", "jwt");
      Rails.ajax({
        url: this.projectUpdateUrl,
        type: "PATCH",
        data: data,
        dataType: "json",
        success: data => {
          this.projectToken = data.jwt;
          this.$notify({
            group: 'success',
            text: this.$t("New FeedBear key is generated successfully"),
          });
          this.$emit("updateJwt", data.jwt);
        },
        error: (status) => {
          this.$notify({
            group: 'alert',
            text: data,
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.beta {
  color: #f84339;
  font-size: 0.9rem;
  font-weight: 600;
  top: -0.8em;
}
.inp-width {
  width: 60%;
}
.childInput {
  width: 100%;
  outline: none;
}
.parentInput {
  width: 100% !important;
}
.codeblock {
  display: flex;
  white-space: pre-wrap;
  overflow-x: scroll;
  @apply bg-neutral-100;
}

code {
  @apply bg-neutral-100;
}

.codeblock code {
  @apply text-neutral-800;
  width: 100%;
}

.form__field--select {
  max-width: 400px;
}

.select_arrow {
  bottom: 1.2rem;
  top: auto;
}
</style>
